/**
 * EmailInboxView — Comprehensive email inbox for synced email activities.
 * Shows emails in a table layout with selection, preview, assign, delete, archive.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Mail,
  Trash2,
  Archive,
  Target,
  CheckCircle,
  Search,
  ArrowRight,
  Paperclip,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  RefreshCw,
  ListChecks,
  Send,
  Reply,
  ReplyAll,
  Forward,
  PenSquare,
  Eye,
  MousePointerClick,
  Sparkles,
  Download,
  FileText,
  Loader2,
  Keyboard,
  Lock,
  Users,
} from 'lucide-react';
import { ComposeEmailModal, ComposeMode } from './ComposeEmailModal';
import { apiClient } from '@/services/apiClient';

interface Activity {
  id: string;
  type: string;
  title: string;
  description?: string;
  direction?: 'inbound' | 'outbound';
  activityDate?: string;
  createdAt?: string;
  opportunityId?: string;
  organizationId?: number;
  emailId?: string;
  sharedWithOrg?: boolean;
  metadata?: {
    from_email?: string;
    to_emails?: string[];
    cc_emails?: string[];
    provider?: string;
    folder?: string;
    importance?: string;
    has_attachments?: boolean;
  };
}

interface Opportunity {
  id: string;
  name: string;
  company: string;
  stage: string;
  amount: number;
}

interface TrackingStats {
  opens: number;
  clicks: number;
  lastOpenAt?: string | null;
  lastClickAt?: string | null;
}

interface EmailInboxViewProps {
  activities: Activity[];
  opportunities: Opportunity[];
  onAssign: (activityId: string, opportunityId: string) => void;
  onCreateOpportunity: (activity: Activity) => void;
  onDelete: (activityIds: string[]) => void;
  onArchive: (activityIds: string[]) => void;
  onAutoAssociate: () => void;
  onSyncEmails: () => Promise<void>;
  onCreateTaskFromEmail?: (activity: Activity) => void;
  onRefreshActivities?: () => void;
  onAssignOrg?: (activityId: string, organizationId: number) => void;
  onToggleShare?: (activityId: string, share: boolean) => void;
}

export function EmailInboxView({
  activities,
  opportunities,
  onAssign,
  onCreateOpportunity,
  onDelete,
  onArchive,
  onAutoAssociate,
  onSyncEmails,
  onCreateTaskFromEmail,
  onRefreshActivities,
  onAssignOrg,
  onToggleShare,
}: EmailInboxViewProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dirFilter, setDirFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>('new');
  const [composeSource, setComposeSource] = useState<Activity | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [trackingStats, setTrackingStats] = useState<Record<string, TrackingStats>>({});
  const trackingFetched = useMemo(() => new Set<string>(), []);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, {
    suggestedOpportunities: Array<{ id: string; name: string; company: string; stage: string; matchReason: string; confidence: number }>;
    suggestedOrganizations: Array<{ id: number; name: string; matchReason: string; confidence: number }>;
    error?: boolean;
  }>>({});
  const aiFetched = useMemo(() => new Set<string>(), []);
  const [fullBodies, setFullBodies] = useState<Record<string, string>>({});
  const [bodyLoading, setBodyLoading] = useState<Record<string, boolean>>({});
  const [bodyError, setBodyError] = useState<Record<string, string>>({});
  const bodyFetched = useMemo(() => new Set<string>(), []);
  const [attachments, setAttachments] = useState<Record<string, Array<{ id: string; name: string; contentType: string; size: number }>>>({});
  const attachmentsFetched = useMemo(() => new Set<string>(), []);
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);

  // Pagination
  const EMAIL_PAGE_SIZE = 25;
  const [emailPage, setEmailPage] = useState(1);

  // Keyboard shortcut: focused email index for j/k navigation
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Refs for keyboard handler to always read current state
  const stateRef = useRef({ focusedIndex, emails: [] as Activity[], expandedId, selectedIds, composeOpen, assigningId, emailPage });
  const actionsRef = useRef({
    onDelete, onArchive, toggleSelect: (_id: string) => {}, setExpandedId: (_id: string | null) => {},
    setComposeMode: (_m: ComposeMode) => {}, setComposeSource: (_a: Activity | null) => {}, setComposeOpen: (_b: boolean) => {},
    fetchFullBody: (_id: string) => {}, fetchAttachments: (_id: string) => {}, fetchTrackingStats: (_id: string) => {},
    fetchAiSuggestions: (_id: string) => {},
  });

  // Fetch AI suggestions for unassigned emails
  const fetchAiSuggestions = useCallback(async (activityId: string) => {
    if (aiFetched.has(activityId)) return;
    aiFetched.add(activityId);
    try {
      const res = await apiClient.get(`/api/crm-activities/ai/suggest-match/${activityId}`);
      if (res.data) {
        setAiSuggestions(prev => ({ ...prev, [activityId]: res.data }));
      } else {
        setAiSuggestions(prev => ({ ...prev, [activityId]: { suggestedOpportunities: [], suggestedOrganizations: [] } }));
      }
    } catch {
      setAiSuggestions(prev => ({ ...prev, [activityId]: { suggestedOpportunities: [], suggestedOrganizations: [], error: true } }));
    }
  }, [aiFetched]);

  // Fetch full email body from Graph API
  const fetchFullBody = useCallback(async (activityId: string, force = false) => {
    if (!force && bodyFetched.has(activityId)) return;
    bodyFetched.add(activityId);
    setBodyLoading(prev => ({ ...prev, [activityId]: true }));
    setBodyError(prev => ({ ...prev, [activityId]: '' }));
    try {
      const res = await apiClient.get(`/api/crm-activities/email/${activityId}/full-body`);
      if (res.data?.body) {
        setFullBodies(prev => ({ ...prev, [activityId]: res.data.body }));
        if (res.data.note) {
          setBodyError(prev => ({ ...prev, [activityId]: res.data.note }));
        }
      } else {
        setBodyError(prev => ({ ...prev, [activityId]: 'No body content returned' }));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load';
      setBodyError(prev => ({ ...prev, [activityId]: msg }));
    } finally {
      setBodyLoading(prev => ({ ...prev, [activityId]: false }));
    }
  }, [bodyFetched]);

  // Fetch attachments list
  const fetchAttachments = useCallback(async (activityId: string) => {
    if (attachmentsFetched.has(activityId)) return;
    attachmentsFetched.add(activityId);
    try {
      const res = await apiClient.get(`/api/crm-activities/email/${activityId}/attachments`);
      if (res.data?.attachments) {
        setAttachments(prev => ({ ...prev, [activityId]: res.data.attachments }));
      }
    } catch {
      // Attachments not available
    }
  }, [attachmentsFetched]);

  // Download an attachment
  const downloadAttachment = useCallback(async (activityId: string, attachmentId: string, filename: string) => {
    setDownloadingAttachment(attachmentId);
    try {
      const res = await apiClient.get(`/api/crm-activities/email/${activityId}/attachments/${attachmentId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      console.error('Failed to download attachment');
    } finally {
      setDownloadingAttachment(null);
    }
  }, []);

  // Fetch tracking stats for an outbound email when expanded
  const fetchTrackingStats = useCallback(async (activityId: string) => {
    if (trackingFetched.has(activityId)) return;
    trackingFetched.add(activityId);
    try {
      const res = await apiClient.get(`/api/crm-activities/tracking/stats/${activityId}`);
      if (res.data) {
        setTrackingStats(prev => ({ ...prev, [activityId]: res.data }));
      }
    } catch {
      // Tracking endpoint may not be available
    }
  }, [trackingFetched]);

  // Filter emails only
  const emails = useMemo(() => {
    return activities
      .filter(a => a.type === 'email')
      .filter(a => {
        if (dirFilter !== 'all' && a.direction !== dirFilter) return false;
        if (statusFilter === 'assigned' && !a.opportunityId) return false;
        if (statusFilter === 'unassigned' && a.opportunityId) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const matchTitle = a.title?.toLowerCase().includes(q);
          const matchFrom = a.metadata?.from_email?.toLowerCase().includes(q);
          const matchTo = a.metadata?.to_emails?.some(e => e.toLowerCase().includes(q));
          const matchDesc = a.description?.toLowerCase().includes(q);
          if (!matchTitle && !matchFrom && !matchTo && !matchDesc) return false;
        }
        return true;
      });
  }, [activities, dirFilter, statusFilter, searchQuery]);

  // Reset page when filters change
  useEffect(() => { setEmailPage(1); }, [dirFilter, statusFilter, searchQuery]);

  const totalEmails = emails.length;
  const totalPages = Math.max(1, Math.ceil(totalEmails / EMAIL_PAGE_SIZE));
  const paginatedEmails = useMemo(
    () => emails.slice(0, emailPage * EMAIL_PAGE_SIZE),
    [emails, emailPage]
  );

  const unassignedCount = activities.filter(a => a.type === 'email' && !a.opportunityId).length;
  const allSelected = paginatedEmails.length > 0 && selectedIds.size === paginatedEmails.length;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedEmails.map(e => e.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    onDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleBulkArchive = () => {
    if (selectedIds.size === 0) return;
    onArchive(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const filteredOpps = useMemo(() => {
    if (!assignSearch) return opportunities.slice(0, 10);
    const q = assignSearch.toLowerCase();
    return opportunities.filter(o =>
      o.name.toLowerCase().includes(q) || o.company.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [opportunities, assignSearch]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const diff = now.getTime() - d.getTime();
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return d.toLocaleDateString([], { weekday: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Keep refs current for keyboard handler
  stateRef.current = { focusedIndex, emails: paginatedEmails, expandedId, selectedIds, composeOpen, assigningId, emailPage };
  actionsRef.current = {
    onDelete, onArchive, toggleSelect, setExpandedId, setComposeMode, setComposeSource, setComposeOpen,
    fetchFullBody, fetchAttachments, fetchTrackingStats, fetchAiSuggestions,
  };

  // Direct keydown listener for sales email shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable) return;

      const { focusedIndex: idx, emails: ems, expandedId: expId, composeOpen: composing, assigningId: assigning } = stateRef.current;
      // Don't interfere when compose modal or assign dropdown is open
      if (composing || assigning) return;

      const email = (idx >= 0 && idx < ems.length) ? ems[idx] : null;
      const acts = actionsRef.current;

      // Arrow keys always work for navigation (even with modifiers held)
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, (stateRef.current.emails.length || 1) - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        return;
      }

      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'j': // next
            e.preventDefault();
            setFocusedIndex(prev => Math.min(prev + 1, (stateRef.current.emails.length || 1) - 1));
            return;
          case 'k': // previous
            e.preventDefault();
            setFocusedIndex(prev => Math.max(prev - 1, 0));
            return;
          case 'o': // open/close detail
          case 'enter':
            if (email) {
              e.preventDefault();
              const newId = expId === email.id ? null : email.id;
              acts.setExpandedId(newId);
              if (newId) {
                acts.fetchFullBody(newId);
                if (email.metadata?.has_attachments) acts.fetchAttachments(newId);
                if (email.direction === 'outbound') acts.fetchTrackingStats(newId);
                if (!email.opportunityId) acts.fetchAiSuggestions(newId);
              }
            }
            return;
          case 'x': // toggle selection
            if (email) { e.preventDefault(); acts.toggleSelect(email.id); }
            return;
          case 'e': // archive
            if (email) {
              e.preventDefault();
              acts.onArchive([email.id]);
              setFocusedIndex(prev => Math.max(0, Math.min(prev, (stateRef.current.emails.length || 1) - 2)));
            }
            return;
          case 'r': // reply
            if (email) {
              e.preventDefault();
              acts.setComposeMode('reply');
              acts.setComposeSource(email);
              acts.setComposeOpen(true);
            }
            return;
          case 'f': // forward
            if (email) {
              e.preventDefault();
              acts.setComposeMode('forward');
              acts.setComposeSource(email);
              acts.setComposeOpen(true);
            }
            return;
          case 'c': // compose new
            e.preventDefault();
            acts.setComposeMode('new');
            acts.setComposeSource(null);
            acts.setComposeOpen(true);
            return;
          case 'u': // close expanded
            if (expId) { e.preventDefault(); acts.setExpandedId(null); }
            return;
        }

        if (e.shiftKey) {
          switch (e.key) {
            case 'D':
            case 'd':
              if (email) {
                e.preventDefault();
                acts.onDelete([email.id]);
                setFocusedIndex(prev => Math.max(0, Math.min(prev, (stateRef.current.emails.length || 1) - 2)));
              }
              return;
            case 'A':
            case 'a':
              if (email) {
                e.preventDefault();
                acts.onArchive([email.id]);
                setFocusedIndex(prev => Math.max(0, Math.min(prev, (stateRef.current.emails.length || 1) - 2)));
              }
              return;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-scroll focused email row into view
  useEffect(() => {
    const el = document.querySelector('[data-email-focused="true"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedIndex]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-3 flex-wrap">
        {/* Search */}
        <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search emails..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Direction filter */}
        <div className="flex items-center gap-1">
          {(['all', 'inbound', 'outbound'] as const).map(d => (
            <button
              key={d}
              onClick={() => setDirFilter(d)}
              className={`px-2.5 py-1 text-xs rounded-full ${
                dirFilter === d ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {d === 'all' ? 'All' : d === 'inbound' ? '← In' : '→ Out'}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-1">
          {(['all', 'unassigned', 'assigned'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 text-xs rounded-full ${
                statusFilter === s ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === 'all' ? 'All' : s === 'unassigned' ? `Unassigned (${unassignedCount})` : 'Assigned'}
            </button>
          ))}
        </div>

        {/* Sync emails */}
        <button
          onClick={async () => {
            setSyncing(true);
            try { await onSyncEmails(); } finally { setSyncing(false); }
          }}
          disabled={syncing}
          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
          title="Sync latest emails from Outlook"
        >
          <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Email'}
        </button>

        {/* Fetch full email bodies for all truncated emails */}
        <button
          onClick={async (e) => {
            e.stopPropagation();
            const btn = e.currentTarget;
            btn.disabled = true;
            btn.textContent = 'Loading...';
            try {
              const res = await apiClient.post('/api/crm-activities/email/batch-fetch-bodies');
              const { updated = 0, errors = 0 } = res.data || {};
              if (updated > 0) {
                // Clear body cache so expanded emails re-render with new content
                bodyFetched.clear();
                setFullBodies({});
                if (onRefreshActivities) onRefreshActivities();
              }
              alert(`Updated ${updated} email(s) with full content.${errors ? ` ${errors} failed.` : ''}`);
            } catch (err: any) {
              alert(err?.response?.data?.detail || 'Failed to fetch email bodies');
            } finally {
              btn.disabled = false;
            }
          }}
          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-green-300 text-green-700 bg-green-50 rounded-lg hover:bg-green-100"
          title="Fetch full email content from Outlook for all synced emails"
        >
          <Download className="h-3 w-3" />
          Load Full Content
        </button>

        {/* Compose new email */}
        <button
          onClick={() => { setComposeMode('new'); setComposeSource(null); setComposeOpen(true); }}
          className="flex items-center gap-1 px-2.5 py-1 text-xs border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
        >
          <PenSquare className="h-3 w-3" />
          Compose
        </button>

        {/* Auto-assign */}
        {unassignedCount > 0 && (
          <button
            onClick={onAutoAssociate}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-orange-300 text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100"
            title="Auto-assign emails to opportunities by matching email domains"
          >
            <RefreshCw className="h-3 w-3" />
            Auto-Assign
          </button>
        )}
      </div>

      {/* Keyboard shortcut hint bar */}
      <div className="flex items-center gap-3 mb-2 px-2 py-1 text-[10px] text-gray-400">
        <Keyboard className="h-3 w-3" />
        <span>↑↓ or j/k navigate</span>
        <span>o open</span>
        <span>x select</span>
        <span>e archive</span>
        <span>r reply</span>
        <span>f forward</span>
        <span>c compose</span>
        <span>Shift+D delete</span>
        <span className="hidden sm:inline">? all shortcuts</span>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-700">{selectedIds.size} selected</span>
          <button
            onClick={handleBulkArchive}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            title="Archive in CRM and move to Archive folder in Outlook"
          >
            <Archive className="h-3 w-3" />
            Archive
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-red-300 text-red-600 rounded hover:bg-red-50"
            title="Delete from CRM and move to Deleted Items in Outlook"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
          <span className="text-[10px] text-gray-400 italic">Syncs with Outlook</span>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      )}

      {/* Email list */}
      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Mail className="h-8 w-8 mb-2" />
            <p className="text-sm">No emails found</p>
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 sticky top-0 overflow-x-auto">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
              />
              <span className="w-16 hidden sm:inline">Dir</span>
              <span className="flex-1">Subject / From</span>
              <span className="w-20 text-right hidden sm:inline">Date</span>
              <span className="w-28 text-right hidden md:inline">Status</span>
            </div>

            {paginatedEmails.map((email, emailIndex) => {
              const isExpanded = expandedId === email.id;
              const isAssigning = assigningId === email.id;
              const isFocused = focusedIndex === emailIndex;
              const linkedOpp = email.opportunityId
                ? opportunities.find(o => o.id === email.opportunityId)
                : null;

              return (
                <div key={email.id} className="group">
                  {/* Email row */}
                  <div
                    data-email-focused={isFocused ? 'true' : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                      selectedIds.has(email.id) ? 'bg-blue-50/50' : isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                    } ${!email.opportunityId ? 'border-l-2 border-l-orange-400' : 'border-l-2 border-l-transparent'} ${
                      isFocused ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                    }`}
                    onClick={() => toggleSelect(email.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(email.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(email.id)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                    />

                    {/* Direction */}
                    <span className={`w-16 flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded text-center hidden sm:inline ${
                      email.direction === 'outbound' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                    }`}>
                      {email.direction === 'outbound' ? '→ Sent' : '← In'}
                    </span>

                    {/* Subject + From — click subject to open email detail */}
                    <div className="flex-1 min-w-0" onClick={(e) => {
                      e.stopPropagation();
                      const newId = isExpanded ? null : email.id;
                      setExpandedId(newId);
                      if (newId) {
                        fetchFullBody(newId);
                        if (email.metadata?.has_attachments) fetchAttachments(newId);
                        if (email.direction === 'outbound') fetchTrackingStats(newId);
                        if (!email.opportunityId) fetchAiSuggestions(newId);
                      }
                    }}>
                      <div className="flex items-center gap-1.5">
                        <p className={`text-sm font-medium truncate hover:underline ${isExpanded ? 'text-blue-700' : 'text-gray-900 hover:text-blue-600'}`}>{email.title || '(No Subject)'}</p>
                        {email.metadata?.has_attachments && (
                          <Paperclip className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {email.direction === 'outbound'
                          ? `To: ${email.metadata?.to_emails?.join(', ') || ''}`
                          : email.metadata?.from_email || ''
                        }
                      </p>
                    </div>

                    {/* Date */}
                    <span className="w-20 text-right text-xs text-gray-500 flex-shrink-0 hidden sm:inline">
                      {formatDate(email.activityDate || email.createdAt)}
                    </span>

                    {/* Status + Tracking */}
                    <div className="w-28 flex items-center justify-end gap-1.5 flex-shrink-0 hidden md:flex">
                      {/* Tracking indicators for outbound */}
                      {email.direction === 'outbound' && trackingStats[email.id] && (trackingStats[email.id].opens > 0 || trackingStats[email.id].clicks > 0) && (
                        <div className="flex items-center gap-1">
                          {trackingStats[email.id].opens > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-blue-500" title={`${trackingStats[email.id].opens} opens`}>
                              <Eye className="h-3 w-3" />{trackingStats[email.id].opens}
                            </span>
                          )}
                          {trackingStats[email.id].clicks > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-green-500" title={`${trackingStats[email.id].clicks} clicks`}>
                              <MousePointerClick className="h-3 w-3" />{trackingStats[email.id].clicks}
                            </span>
                          )}
                        </div>
                      )}
                      {email.opportunityId ? (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-600 rounded-full truncate max-w-[80px]">
                          {linkedOpp?.name || 'Linked'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-600 rounded-full">
                          Unassigned
                        </span>
                      )}
                      {email.sharedWithOrg ? (
                        <Users
                          className="h-3 w-3 text-blue-500 flex-shrink-0"
                          aria-label="Shared with organization"
                        >
                          <title>Shared with organization</title>
                        </Users>
                      ) : (
                        <Lock
                          className="h-3 w-3 text-gray-400 flex-shrink-0"
                          aria-label="Private to you"
                        >
                          <title>Private to you</title>
                        </Lock>
                      )}
                    </div>

                    {/* Open email detail button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newId = isExpanded ? null : email.id;
                        setExpandedId(newId);
                        if (newId) {
                          fetchFullBody(newId);
                          if (email.metadata?.has_attachments) fetchAttachments(newId);
                          if (email.direction === 'outbound') fetchTrackingStats(newId);
                          if (!email.opportunityId) fetchAiSuggestions(newId);
                        }
                      }}
                      className="p-1 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
                      title={isExpanded ? 'Close email' : 'Open email'}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (() => {
                    // Build conversation thread: find related emails by subject
                    const baseSubject = (email.title || '').replace(/^(Re:\s*|Fwd:\s*|Fw:\s*)+/i, '').trim().toLowerCase();
                    const threadEmails = activities
                      .filter(a => a.type === 'email' && a.id !== email.id)
                      .filter(a => {
                        const aSubject = (a.title || '').replace(/^(Re:\s*|Fwd:\s*|Fw:\s*)+/i, '').trim().toLowerCase();
                        return aSubject === baseSubject && baseSubject.length > 3;
                      })
                      .sort((a, b) => new Date(a.activityDate || a.createdAt || 0).getTime() - new Date(b.activityDate || b.createdAt || 0).getTime());

                    const emailSuggestions = aiSuggestions[email.id];

                    return (
                    <div className="border-t border-gray-100">
                      <div className="flex flex-col md:flex-row">
                        {/* Left: Email content — takes majority of width */}
                        <div className="flex-1 min-w-0 p-3 sm:p-4 max-h-[50vh] sm:max-h-[600px] overflow-y-auto">
                          {/* Email headers */}
                          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-gray-600 mb-4">
                            {email.metadata?.from_email && (
                              <>
                                <span className="text-gray-400 font-medium">From</span>
                                <span>{email.metadata.from_email}</span>
                              </>
                            )}
                            {email.metadata?.to_emails && email.metadata.to_emails.length > 0 && (
                              <>
                                <span className="text-gray-400 font-medium">To</span>
                                <span>{email.metadata.to_emails.join(', ')}</span>
                              </>
                            )}
                            {email.metadata?.cc_emails && email.metadata.cc_emails.length > 0 && (
                              <>
                                <span className="text-gray-400 font-medium">Cc</span>
                                <span>{email.metadata.cc_emails.join(', ')}</span>
                              </>
                            )}
                            <span className="text-gray-400 font-medium">Date</span>
                            <span>{email.activityDate ? new Date(email.activityDate).toLocaleString() : email.createdAt ? new Date(email.createdAt).toLocaleString() : ''}</span>
                          </div>

                          {/* Attachments — direct download from Graph API */}
                          {email.metadata?.has_attachments && (
                            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Paperclip className="h-3.5 w-3.5 text-amber-600" />
                                <span className="text-xs font-semibold text-amber-700">Attachments</span>
                              </div>
                              {attachments[email.id] ? (
                                attachments[email.id].length > 0 ? (
                                  <div className="space-y-1">
                                    {attachments[email.id].map(att => (
                                      <button
                                        key={att.id}
                                        onClick={(e) => { e.stopPropagation(); downloadAttachment(email.id, att.id, att.name); }}
                                        disabled={downloadingAttachment === att.id}
                                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs bg-white rounded border border-amber-100 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                      >
                                        {downloadingAttachment === att.id ? (
                                          <Loader2 className="h-3 w-3 text-amber-500 animate-spin" />
                                        ) : (
                                          <FileText className="h-3 w-3 text-amber-500" />
                                        )}
                                        <span className="flex-1 truncate font-medium text-gray-700">{att.name}</span>
                                        <span className="text-[10px] text-gray-400">{att.size ? `${(att.size / 1024).toFixed(0)}KB` : ''}</span>
                                        <Download className="h-3 w-3 text-gray-400" />
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-amber-600">Inline images only — no downloadable attachments</p>
                                )
                              ) : (
                                <p className="text-xs text-amber-500 flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" /> Loading attachments...
                                </p>
                              )}
                            </div>
                          )}

                          {/* Tracking stats bar for outbound */}
                          {email.direction === 'outbound' && trackingStats[email.id] && (
                            <div className="flex items-center gap-4 mb-3 px-3 py-2 bg-blue-50 rounded-lg">
                              <span className="flex items-center gap-1.5 text-xs">
                                <Eye className="h-3.5 w-3.5 text-blue-500" />
                                <span className="font-medium text-gray-700">{trackingStats[email.id].opens}</span>
                                <span className="text-gray-500">{trackingStats[email.id].opens === 1 ? 'open' : 'opens'}</span>
                                {trackingStats[email.id].lastOpenAt && (
                                  <span className="text-gray-400 text-[10px]">· {new Date(trackingStats[email.id].lastOpenAt!).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                              </span>
                              <span className="flex items-center gap-1.5 text-xs">
                                <MousePointerClick className="h-3.5 w-3.5 text-green-500" />
                                <span className="font-medium text-gray-700">{trackingStats[email.id].clicks}</span>
                                <span className="text-gray-500">{trackingStats[email.id].clicks === 1 ? 'click' : 'clicks'}</span>
                                {trackingStats[email.id].lastClickAt && (
                                  <span className="text-gray-400 text-[10px]">· {new Date(trackingStats[email.id].lastClickAt!).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                              </span>
                            </div>
                          )}

                          {/* Email body — full height, uses fetched full body when available */}
                          {bodyLoading[email.id] && !fullBodies[email.id] && (
                            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 mb-2">
                              <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                              <span className="text-sm text-gray-500">Loading full email content...</span>
                            </div>
                          )}
                          {(() => {
                            const bodyHtml = fullBodies[email.id] || email.description;
                            if (!bodyHtml) return null;
                            const isShort = !fullBodies[email.id] && bodyHtml.length < 300 && !bodyHtml.includes('<');
                            return (
                              <>
                                <div
                                  className="text-sm text-gray-700 bg-white rounded-lg border border-gray-200 p-4 prose prose-sm max-w-none [&_img]:max-w-full [&_table]:text-xs"
                                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                                />
                                {isShort && !bodyLoading[email.id] && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); fetchFullBody(email.id, true); }}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                      Load full email from Outlook
                                    </button>
                                    {bodyError[email.id] && (
                                      <span className="text-xs text-red-500">{bodyError[email.id]}</span>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          {/* Conversation thread */}
                          {threadEmails.length > 0 && (
                            <div className="mt-5 pt-4 border-t border-gray-200">
                              <h4 className="text-xs font-semibold text-gray-500 mb-3 flex items-center gap-1.5 uppercase tracking-wider">
                                <Mail className="h-3.5 w-3.5" />
                                Thread History — {threadEmails.length} earlier {threadEmails.length === 1 ? 'message' : 'messages'}
                              </h4>
                              <div className="space-y-3">
                                {threadEmails.map(threadMsg => (
                                  <div
                                    key={threadMsg.id}
                                    className={`rounded-lg border p-3 ${
                                      threadMsg.direction === 'outbound'
                                        ? 'bg-blue-50/50 border-blue-200 ml-6'
                                        : 'bg-white border-gray-200 mr-6'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className={`font-semibold ${threadMsg.direction === 'outbound' ? 'text-blue-700' : 'text-gray-800'}`}>
                                          {threadMsg.direction === 'outbound' ? 'You' : threadMsg.metadata?.from_email || 'Unknown'}
                                        </span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                          threadMsg.direction === 'outbound' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                                        }`}>
                                          {threadMsg.direction === 'outbound' ? '→ Sent' : '← Received'}
                                        </span>
                                        {threadMsg.metadata?.has_attachments && (
                                          <Paperclip className="h-3 w-3 text-gray-400" title="Has attachments" />
                                        )}
                                      </div>
                                      <span className="text-[10px] text-gray-400">
                                        {threadMsg.activityDate ? new Date(threadMsg.activityDate).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                      </span>
                                    </div>
                                    {threadMsg.metadata?.to_emails && (
                                      <p className="text-[10px] text-gray-400 mb-2">To: {threadMsg.metadata.to_emails.join(', ')}</p>
                                    )}
                                    {threadMsg.description && (
                                      <div
                                        className="text-xs text-gray-600 prose prose-xs max-w-none [&_img]:max-w-full"
                                        dangerouslySetInnerHTML={{ __html: threadMsg.description }}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right sidebar — Actions + AI suggestions */}
                        <div className="w-full md:w-56 flex-shrink-0 border-t md:border-t-0 md:border-l border-gray-200 p-3 sm:p-4 bg-gray-50/50 space-y-3 max-h-[40vh] sm:max-h-[600px] overflow-y-auto">
                          {/* Assignment status */}
                          {email.opportunityId ? (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-[10px] text-green-600 font-medium mb-1">Linked to Opportunity</p>
                              <p className="text-sm font-medium text-green-800 truncate">
                                {linkedOpp?.name || email.opportunityId}
                              </p>
                              {linkedOpp && (
                                <p className="text-xs text-green-600 mt-0.5">{linkedOpp.company} · {linkedOpp.stage}</p>
                              )}
                            </div>
                          ) : isAssigning ? (
                            <div className="border border-blue-200 rounded-lg p-2 space-y-1.5">
                              <input
                                type="text"
                                value={assignSearch}
                                onChange={e => setAssignSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                                onClick={e => e.stopPropagation()}
                              />
                              <div className="max-h-28 overflow-y-auto space-y-0.5">
                                {filteredOpps.map(opp => (
                                  <button
                                    key={opp.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAssign(email.id, opp.id);
                                      setAssigningId(null);
                                      setAssignSearch('');
                                    }}
                                    className="w-full text-left px-2 py-1 text-xs rounded hover:bg-blue-50 truncate"
                                  >
                                    <span className="font-medium">{opp.name}</span>
                                    <span className="text-gray-400 ml-1">· {opp.company}</span>
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); onCreateOpportunity(email); setAssigningId(null); }}
                                className="w-full flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <Plus className="h-3 w-3" /> New Opportunity
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAssigningId(null); setAssignSearch(''); }}
                                className="w-full text-xs text-gray-400 hover:text-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {/* AI Suggested opportunity matches */}
                              {emailSuggestions?.suggestedOpportunities?.length > 0 && (
                                <div className="border border-violet-200 bg-violet-50 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-violet-700 mb-2 flex items-center gap-1.5">
                                    <Sparkles className="h-3.5 w-3.5" />
                                    AI Suggested Matches
                                  </p>
                                  <div className="space-y-1.5">
                                    {emailSuggestions.suggestedOpportunities.slice(0, 3).map(sug => (
                                      <button
                                        key={sug.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onAssign(email.id, sug.id);
                                        }}
                                        className="w-full text-left px-2.5 py-2 text-xs rounded-md bg-white hover:bg-violet-100 border border-violet-100 hover:border-violet-300 transition-colors shadow-sm"
                                        title={sug.matchReason}
                                      >
                                        <div className="flex items-center justify-between mb-0.5">
                                          <span className="font-semibold text-gray-800 truncate">{sug.name}</span>
                                          <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded ml-1">{Math.round(sug.confidence * 100)}%</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500">{sug.company} · {sug.stage}</p>
                                        <p className="text-[10px] text-violet-500 mt-0.5 italic">{sug.matchReason}</p>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* AI Matched organizations */}
                              {emailSuggestions?.suggestedOrganizations?.length > 0 && (
                                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-emerald-700 mb-2">Matched Organizations</p>
                                  <div className="space-y-1">
                                    {emailSuggestions.suggestedOrganizations.slice(0, 3).map(org => (
                                      <button
                                        key={org.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (onAssignOrg) onAssignOrg(email.id, org.id);
                                        }}
                                        className="w-full text-left text-xs bg-white rounded border border-emerald-100 px-2.5 py-1.5 hover:bg-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
                                      >
                                        <div className="flex items-center justify-between">
                                          <p className="font-medium text-gray-800">{org.name}</p>
                                          <span className="text-[10px] font-medium text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded ml-1">{Math.round(org.confidence * 100)}%</span>
                                        </div>
                                        <p className="text-[10px] text-emerald-600 italic">{org.matchReason}</p>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* AI state: loading, error, or no matches */}
                              {!emailSuggestions && aiFetched.has(email.id) && (
                                <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 text-center">
                                  <Loader2 className="h-4 w-4 text-gray-400 animate-spin mx-auto mb-1" />
                                  <p className="text-xs text-gray-400">Analyzing email...</p>
                                </div>
                              )}
                              {emailSuggestions?.error && (
                                <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-center">
                                  <p className="text-xs text-red-500">Could not analyze — assign manually</p>
                                </div>
                              )}
                              {emailSuggestions && !emailSuggestions.error && emailSuggestions.suggestedOpportunities?.length === 0 && emailSuggestions.suggestedOrganizations?.length === 0 && (
                                <div className="border border-gray-200 bg-gray-50 rounded-lg p-3 text-center">
                                  <p className="text-xs text-gray-400">No matching opportunities found — assign manually</p>
                                </div>
                              )}

                              {/* Manual assign button */}
                              <button
                                onClick={(e) => { e.stopPropagation(); setAssigningId(email.id); }}
                                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                              >
                                <Target className="h-3.5 w-3.5" />
                                {emailSuggestions?.suggestedOpportunities?.length ? 'Browse All Opportunities' : 'Assign to Opportunity'}
                              </button>
                            </div>
                          )}

                          {/* Share with org / Make private */}
                          {onToggleShare && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleShare(email.id, !email.sharedWithOrg);
                              }}
                              className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg ${
                                email.sharedWithOrg
                                  ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100'
                                  : 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100'
                              }`}
                              title={
                                email.sharedWithOrg
                                  ? 'Hide this email from other users in your org'
                                  : 'Let other users in your org see this email'
                              }
                            >
                              {email.sharedWithOrg ? (
                                <>
                                  <Lock className="h-3 w-3" />
                                  Make Private
                                </>
                              ) : (
                                <>
                                  <Users className="h-3 w-3" />
                                  Share with Organization
                                </>
                              )}
                            </button>
                          )}

                          {/* Create Task from email */}
                          {onCreateTaskFromEmail && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onCreateTaskFromEmail(email); }}
                              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100"
                            >
                              <ListChecks className="h-3 w-3" />
                              Create Task
                            </button>
                          )}

                          {/* Reply / Forward */}
                          <div className="flex gap-1.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); setComposeMode('reply'); setComposeSource(email as any); setComposeOpen(true); }}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                              title="Reply"
                            >
                              <Reply className="h-3 w-3" />
                              Reply
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setComposeMode('replyAll'); setComposeSource(email as any); setComposeOpen(true); }}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                              title="Reply All"
                            >
                              <ReplyAll className="h-3 w-3" />
                              All
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setComposeMode('forward'); setComposeSource(email as any); setComposeOpen(true); }}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                              title="Forward"
                            >
                              <Forward className="h-3 w-3" />
                              Fwd
                            </button>
                          </div>

                          {/* Delete / Archive */}
                          <div className="flex gap-1.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); onArchive([email.id]); }}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                              title="Archive in CRM and move to Archive in Outlook"
                            >
                              <Archive className="h-3 w-3" />
                              Archive
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); onDelete([email.id]); }}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-600 bg-red-50 rounded hover:bg-red-100"
                              title="Delete from CRM and move to Deleted Items in Outlook"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Load more */}
      {emailPage * EMAIL_PAGE_SIZE < totalEmails && (
        <div className="flex justify-center mt-3">
          <button
            onClick={() => setEmailPage(p => p + 1)}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            Load more ({totalEmails - emailPage * EMAIL_PAGE_SIZE} remaining)
          </button>
        </div>
      )}

      {/* Footer stats */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-2 text-xs text-gray-500 gap-1">
        <span>Showing {paginatedEmails.length} of {totalEmails} emails</span>
        <span>
          {unassignedCount} unassigned · {activities.filter(a => a.type === 'email' && a.opportunityId).length} linked
        </span>
      </div>

      {/* Compose Email Modal */}
      <ComposeEmailModal
        isOpen={composeOpen}
        onClose={() => { setComposeOpen(false); setComposeSource(null); }}
        mode={composeMode}
        sourceActivity={composeSource}
        opportunities={opportunities}
        onSent={() => {
          setComposeOpen(false);
          setComposeSource(null);
          onRefreshActivities?.();
        }}
      />
    </div>
  );
}
